/* eslint-disable react-redux/useSelector-prefer-selectors */
import { useAppDispatch, useAppSelector } from '../../../../stores/redux.store';
import {
  usePageState,
  isRedirectResult,
  BasePageState,
  BaseRedirectState,
  isPageStateResult,
  isSasStateEndpointEnabled,
} from '../../../../utilities/hooks/usePageState';
import hasNoInjectedServerState from '../../../../utilities/has-no-injected-server-state';
import { JWT, OidcContext, RedirectType } from '../../../../types';
import { useEffect } from 'react';
import { updateMicrobranding } from '../../../../actions/microbranding-actions';
import { welcomeSetState } from '../../../../actions/welcome-actions';
import { useNavigateSoftOrHard } from '../../../../utilities/hooks/useNavigateSoftOrHard';

export type SignupWelcomeStateResult = BaseRedirectState | SignupWelcomePageState;

export interface SignupWelcomePageState extends BasePageState {
  csrfToken: string;
  recoveryToken?: JWT;
  redirectType: RedirectType;
  email: string;
  displayName?: string;
  shouldSkipCaptcha: boolean;
  oidcContext: OidcContext;
}

const usePreloadedState = () => {
  const csrfToken = useAppSelector(state => state.csrfToken);
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const isEmbedded = useAppSelector(state => Boolean(state.microbranding.isEmbedded));
  const marketingConsentConfig = useAppSelector(state => state.marketingConsent.config);

  const { recaptchaEnable, auth0Config } = useAppSelector(state => state.appConfig);
  const {
    displayName,
    email,
    error,
    fieldErrors,
    formError,
    isLoading,
    recoveryToken,
    redirectTo,
    redirectType,
    shouldSkipCaptcha,
  } = useAppSelector(state => state.welcome);

  return {
    auth0Config,
    csrfToken,
    displayName,
    email: email || '',
    error,
    fieldErrors,
    formError,
    isEmbedded,
    isLoading,
    marketingConsentConfig,
    oidcContext,
    recaptchaEnable,
    recoveryToken,
    redirectTo,
    redirectType,
    shouldSkipCaptcha,
  };
};

export const useWelcomeState = () => {
  const noServerState = isSasStateEndpointEnabled() && hasNoInjectedServerState();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();

  const {
    state: stateFromApi,
    isLoading,
    isError,
    error,
  } = usePageState('/signup/welcome/state', {
    enabled: noServerState,
  });

  useEffect(() => {
    if (isPageStateResult(stateFromApi)) {
      dispatch(updateMicrobranding(stateFromApi.oidcContext));
      dispatch(welcomeSetState(stateFromApi));
    }
  }, [dispatch, stateFromApi]);

  const preloadedState = usePreloadedState();

  if (isRedirectResult(stateFromApi)) {
    navigate(stateFromApi.uri);

    return {
      state: undefined,
      isLoading: true,
      isError: false,
    };
  }

  if (noServerState) {
    return {
      state: {
        ...preloadedState,
        ...stateFromApi,
      },
      isLoading,
      isError,
      error,
    };
  }

  return {
    state: preloadedState,
    isLoading: false,
    isError: false,
    error,
  };
};
